'use client';

// # START_MODULE_CONTRACT_SCHOOL_REVIEWS:
// # PURPOSE: [Секция отзывов о школе: список опубликованных + форма подачи (премодерация).]
// # SCOPE: [Каталог учебных центров /tools/uchebne-centry; источник public.school_reviews + RPC submit_school_review.]
// # SEMANTIC: [Читает published-отзывы через anon (RLS); подача — POST /api/school-reviews (сервер знает IP).]
// # END_MODULE_CONTRACT_SCHOOL_REVIEWS:

import { useEffect, useState } from 'react';
import { Star, MessageSquarePlus, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { createClient } from '@oxpa/shared/lib/supabase/client';

interface Review {
  id: string;
  author_alias: string;
  rating: number;
  text: string;
  created_at: string;
}

export function SchoolReviews({ schoolId, schoolName }: { schoolId: string; schoolName: string }) {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [alias, setAlias] = useState('');
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    const supabase = createClient();
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('school_reviews')
          .select('id, author_alias, rating, text, created_at')
          .eq('school_id', schoolId)
          .eq('status', 'published')
          .order('created_at', { ascending: false });
        if (!cancelled) setReviews((data ?? []) as Review[]);
      } catch {
        // игнорируем ошибку загрузки — покажем пустой список
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [schoolId]);

  async function submit() {
    setMessage(null);
    if (!alias.trim() || !text.trim()) {
      setMessage({ ok: false, text: 'Укажите имя и текст отзыва' });
      return;
    }
    if (!consent) {
      setMessage({ ok: false, text: 'Подтвердите согласие на обработку данных' });
      return;
    }
    if (text.trim().length > 2000) {
      setMessage({ ok: false, text: 'Отзыв длиннее 2000 символов' });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/school-reviews', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ school_id: schoolId, author_alias: alias.trim(), rating, text: text.trim() }),
      });
      const json = await res.json();
      if (json.ok) {
        setMessage({ ok: true, text: 'Спасибо! Отзыв отправлен на модерацию и появится после проверки.' });
        setAlias('');
        setText('');
        setConsent(false);
      } else {
        setMessage({ ok: false, text: json.error || 'Не удалось отправить отзыв' });
      }
    } catch {
      setMessage({ ok: false, text: 'Сетевая ошибка. Попробуйте ещё раз.' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mt-4 border-t border-[hsl(var(--portal-border))] pt-4">
      <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-[hsl(var(--portal-text-secondary))] mb-2">
        <MessageSquarePlus className="w-4 h-4 text-[hsl(var(--portal-accent))]" strokeWidth={1.5} />
        Отзывы
      </div>

      {loading ? (
        <div className="text-sm text-[hsl(var(--portal-text-muted))] font-sans">Загрузка отзывов…</div>
      ) : reviews.length === 0 ? (
        <div className="text-sm text-[hsl(var(--portal-text-muted))] font-sans">Пока нет отзывов. Будьте первым!</div>
      ) : (
        <ul className="space-y-3">
          {reviews.map((r) => (
            <li key={r.id} className="border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-surface))] p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-[hsl(var(--portal-text))] font-sans">{r.author_alias}</span>
                <span className="inline-flex items-center gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      strokeWidth={1.5}
                      className={
                        'w-3.5 h-3.5 ' +
                        (i < r.rating
                          ? 'text-[hsl(var(--portal-accent))] fill-[hsl(var(--portal-accent))]'
                          : 'text-[hsl(var(--portal-text-dim))]')
                      }
                    />
                  ))}
                </span>
              </div>
              <p className="text-sm text-[hsl(var(--portal-text-secondary))] font-sans mt-1">{r.text}</p>
            </li>
          ))}
        </ul>
      )}

      {/* Форма подачи */}
      <div className="mt-4 border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-surface))] p-3">
        <div className="flex items-center gap-1 mb-2">
          <span className="text-sm text-[hsl(var(--portal-text-secondary))] font-sans mr-1">Оценка:</span>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setRating(n)}
              aria-label={'Оценка ' + n}
              className="p-0.5"
            >
              <Star
                strokeWidth={1.5}
                className={
                  'w-5 h-5 ' +
                  (n <= rating
                    ? 'text-[hsl(var(--portal-accent))] fill-[hsl(var(--portal-accent))]'
                    : 'text-[hsl(var(--portal-text-dim))]')
                }
              />
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Ваш псевдоним (например, Иван)"
          value={alias}
          onChange={(e) => setAlias(e.target.value)}
          maxLength={60}
          className="w-full mb-2 px-3 py-2 border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-surface))] text-[hsl(var(--portal-text))] placeholder:text-[hsl(var(--portal-text-dim))] font-sans text-sm focus:outline-none focus:border-[hsl(var(--portal-accent))] transition-colors"
        />
        <textarea
          placeholder="Расскажите о школе (без телефонов и e-mail)"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          maxLength={2000}
          className="w-full mb-2 px-3 py-2 border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-surface))] text-[hsl(var(--portal-text))] placeholder:text-[hsl(var(--portal-text-dim))] font-sans text-sm focus:outline-none focus:border-[hsl(var(--portal-accent))] transition-colors resize-none"
        />
        <label className="flex items-start gap-2 text-xs text-[hsl(var(--portal-text-muted))] font-sans cursor-pointer mb-3">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 w-4 h-4 border border-[hsl(var(--portal-border-hover))] bg-[hsl(var(--portal-surface))] accent-[hsl(var(--portal-accent))]"
          />
          <span>
            Согласен(на) на обработку персональных данных (п. <span className="font-mono tabular-nums">9</span> ст.{" "}
            <span className="font-mono tabular-nums">9</span> <span className="font-mono tabular-nums">152-ФЗ</span>) для публикации отзыва.
          </span>
        </label>
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="inline-flex items-center gap-2 bg-[hsl(var(--portal-accent))] text-[hsl(var(--portal-accent-on))] text-sm font-bold uppercase tracking-[0.15em] px-4 py-2 hover:bg-[hsl(var(--portal-accent-hover))] disabled:opacity-50 transition-colors"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.5} />}
          Отправить отзыв
        </button>
        {message && (
          <p
            className={
              'text-sm mt-2 font-sans ' +
              (message.ok ? 'text-[hsl(var(--portal-accent))]' : 'text-[hsl(var(--portal-danger))]')
            }
          >
            {message.text}
          </p>
        )}
      </div>
    </div>
  );
}
